import { makeObservable, observable, flow } from "mobx";
import { IBlobSasModel } from '@common/models';
import * as enums from '@common/models/Enums';
import { guard } from "@common/utils";
import { ContainerClient, newPipeline } from '@azure/storage-blob';
import { StorageService } from "@common/services/storageService";

interface IAzureBlobStoreInit {
    containerName: enums.BlobEntityType,
}

/**
 * Manage using azure/storage-blog and uploading/downloading to azure blob storage
 */
export class AzureBlobStore implements IAzureBlobStoreInit {
    @observable containerName!: enums.BlobEntityType;
    @observable uploadingFiles: boolean = true;

    constructor(init: IAzureBlobStoreInit) {
        Object.assign(this, init);
        makeObservable(this, undefined, { autoBind: true });
    }

    uploadDataUrl = flow(function* (this: AzureBlobStore, dataUrl: string, fileName: string, type: enums.MimeType, modelId: number = 0, isPublic: boolean = false) {
        guard.fromNull(dataUrl, "dataUrl");
        guard.fromNull(fileName, "fileName");
        guard.fromNull(type, "type");
        this.uploadingFiles = true;
        const res: Response = yield fetch(dataUrl);
        const blob: Blob = yield res.blob();
        const file = new File([blob], fileName, { type: type.valueOf() });
        const sas: IBlobSasModel = yield this.uploadFileImpl(file, modelId, isPublic);
        this.uploadingFiles = false;
        return sas;
    });

    /**
     * Upload the file passed in to azure blob storage and return the IBlobSasModel object, you need this to update files in Photos and StorageContents tables
     */
    uploadFile = flow(function* (this: AzureBlobStore, file: File, modelId: number = 0, isPublic: boolean = false) {
        guard.fromNull(file, "file");
        this.uploadingFiles = true;
        const sas: IBlobSasModel = yield this.uploadFileImpl(file, modelId, isPublic);
        this.uploadingFiles = false;
        return sas;
    });

    /**
     * Upload the files in the store to azure blob storage and return their IBlobSasModel object, you need this to update files in Photos and StorageContents tables
     */
    uploadFiles = flow(function* (this: AzureBlobStore, files: FileList, modelId: number = 0, isPublic: boolean = false) {
        guard.fromNull(files, "files");
        guard.fromEmpty([...files], "files");
        this.uploadingFiles = true;
        const ret: IBlobSasModel[] = [];
        for (const file of files) {
            const sas = yield this.uploadFileImpl(file, modelId, isPublic)
            ret.push(sas);
        }
        this.uploadingFiles = false;
        return ret;
    });

    private async uploadFileImpl(file: File, modelId: number, isPublic: boolean = false): Promise<IBlobSasModel> {
        const sas: IBlobSasModel = await StorageService.getSas(file, modelId, isPublic, this.containerName);
        await new ContainerClient(sas.sasUrl.replace(sas.fileName, "") + sas.sasToken, newPipeline()).getBlockBlobClient(sas.fileName).uploadData(file);
        return sas;
    }
}
